<template>
  <div v-if="storeCart.hydrated && serviceproducts !== null">
    <c-box
      v-if="submitting"
      z-index="900000"
      position="fixed"
      background-color="#000"
      left="0"
      top="0"
      width="100%"
      height="100%"
      opacity="0.5"
      cursor="wait"
    ></c-box>

    <mobile-tab-menu
      v-chakra
      :d="{ lg: 'none' }"
      :mt="{ base: '3xl', sm: '4xl' }"
      :mb="{ base: 'md', sm: 'lg' }"
      :currentPage="translatedCurrentPage"
    >
      <template>
        <order-tab-container
          :current-step="getStep()"
          :cart-has-no-products="cartHasNoProducts"
          :is-user-permitted-to-order-service-products="isUserPermittedToOrderServiceProducts"
          :is-error="isError"
        />
      </template>
    </mobile-tab-menu>

    <c-heading
      as="h1"
      size="xl"
      :mt="{ lg: '4xl' }"
      :mb="{ lg: '3xl' }"
      fontWeight="normal"
      :display="{ base: 'block', sm: 'flex' }"
      align-items="center"
    >
      <c-text as="span" data-cy="shopping-cart-title">{{ $t("shoppingCart.title") }}</c-text>
      <c-button
        v-if="getStep() === 4"
        background="brand.beige"
        borderColor="brand.beige"
        :mt="{ base: 'xl', sm: '0' }"
        :ml="{ sm: 'auto' }"
        :display="{ base: 'block', sm: 'inherit' }"
        data-cy="print_order"
        class="button-print"
        onclick="window.print(); return false;"
        v-chakra="{
          '@media print': {
            display: 'none',
          },
        }"
      >
        {{ $t("orderConfirmation.print") }}
      </c-button>
    </c-heading>
    <c-box class="row">
      <div class="col-12">
        <c-flex
          :mx="{ base: '-md', sm: '0' }"
          :mt="{ base: 'md', lg: '3xl' }"
          :mb="{ base: 'md', lg: '3xl' }"
          :flex-direction="{ base: 'column', '2xl': 'row' }"
          :align-items="{ '2xl': 'center' }"
        >
          <c-flex :display="{ base: 'none', lg: 'flex' }">
            <order-tab-container
              :current-step="getStep()"
              :cart-has-no-products="cartHasNoProducts"
              :is-user-permitted-to-order-service-products="isUserPermittedToOrderServiceProducts"
              :is-error="isError"
            />
          </c-flex>

          <c-box
            v-if="getStep() === 1 && shoppingCartNotEmpty"
            ml="auto"
            :mt="{ md: 'xl', '2xl': '0' }"
          >
            <CheckAvailability
              @checking-availability="
                (value) => {
                  checkingAvailability = value;
                }
              "
            />
          </c-box>
        </c-flex>

        <form novalidate @submit.prevent="submit()">
          <div v-if="getStep() === 1">
            <ShoppingCart
              :checkingAvailability="checkingAvailability"
              :onFieldBlur="onFieldBlur"
              :onFieldChange="onFieldChange"
              :isErrorField="isErrorField"
              :getFieldClasses="getFieldClasses"
              :cart="cart"
              @set-project-discount-code="setProjectDiscountCode"
              @set-project-id-disabled="setProjectIdDisabled"
              @set-project-id-selected="setProjectIdSelected"
            />
          </div>

          <div v-if="getStep() === 2">
            <AdditionalServices
              :isUserPermittedToOrderServiceProducts="isUserPermittedToOrderServiceProducts"
              :cart="cart"
              :partnerInfo="partnerInfo"
              :serviceproducts="serviceproducts"
            />
          </div>

          <div v-if="getStep() === 3">
            <DeliveryDetails
              v-if="getStep() === 3"
              ref="deliveryDetails"
              :cart="cart"
              :onFieldBlur="onFieldBlur"
              :getFieldClasses="getFieldClasses"
              :isErrorField="isErrorField"
              :getValidationField="getValidationField"
              :onFieldChange="onFieldChange"
              :phoneNumberWithoutCountryCallingCode="phoneNumberWithoutCountryCallingCode"
              :defaultPhoneNumberCountryCode="defaultPhoneNumberCountryCode"
              :phoneNumberInputValue="phoneNumberInputValue"
              :datePickerState="datePickerState"
              :setPhoneNumberInput="setPhoneNumberInput"
              :setPhoneNumber="setPhoneNumber"
              :$v="$v"
              :deliveryMethodList="getDeliveryMethodList()"
              :projectInputForValidator="projectInputForValidator"
              :updateDeliveryDelay="updateDeliveryDelay"
              :deliveryDelay="deliveryDelay"
              :countryAndZipCodeUpdated="handleCountryAndZipCodeValidationAndCityUpdate"
              :errors="errors"
            />
          </div>

          <div v-if="getStep() === 4">
            <OrderingConfirmation
              :cart="cart"
              :deliveryMethodList="getDeliveryMethodList()"
              :errors="errors"
            />
          </div>

          <div v-if="getStep() === 4">
            <c-flex
              :px="{ base: 'xs', sm: 'xl', md: '2xl' }"
              :pt="{ base: 'xs', sm: 'xl', md: '2xl' }"
            >
              <input id="confirm" v-model.trim="confirm" type="checkbox" class="form-check-input" />
              <label class="form-check-label" for="confirm">
                {{ $t("ordering.confirm") }}
              </label>
              <div v-if="confirm && isOrderTooBig" class="invalid-feedback">
                {{ $t("ordering.tooBigOrder") }}
              </div>
            </c-flex>
            <c-flex
              :px="{ base: 'xs', sm: 'xl', md: '2xl' }"
              :pb="{ base: 'xs', sm: 'xl', md: '2xl' }"
            >
              <input
                id="confirmCustomerReference"
                v-model.trim="confirmCustomerReference"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="confirmCustomerReference">
                {{ $t("ordering.confirmCustomerReference") }}
              </label>
            </c-flex>
          </div>

          <c-box
            class="buttons"
            :mt="{ base: 'md', md: '3xl' }"
            :mx="{ base: '-15px', sm: '0' }"
            :display="{ base: 'grid', md: 'block' }"
            gap="sm"
            :grid-template-areas="`
              'draft draft'
              'previous next'
            `"
          >
            <router-link
              :to="{
                path: '/shopping_cart',
                query: { step: getPreviousStep() },
              }"
              v-chakra
              grid-area="previous"
              :mr="{ base: 'auto', md: '0' }"
            >
              <c-button :disabled="getStep() === 1">
                {{ $t("ordering.previous") }}
              </c-button>
            </router-link>
            <c-button
              :ml="{ md: 'md' }"
              :mr="{ base: 'auto', md: '0' }"
              :disabled="!isStoringAsDraftAllowed"
              data-cy="saveOrderDraftButton"
              @click="saveDraft()"
              grid-area="draft"
            >
              <c-icon name="save" color="brand.purple" mr="xs" size="1.25rem" />
              {{ $t("ordering.buttons.saveDraft") }}
            </c-button>

            <router-link
              :to="{ path: '/shopping_cart', query: { step: getNextStep() } }"
              v-if="getStep() < 4"
              v-chakra
              grid-area="next"
              :ml="{ base: 'auto', md: '0' }"
            >
              <c-button
                background="brand.beige"
                borderColor="brand.beige"
                float="right"
                :disabled="
                  (getStep() === 3 && isError) ||
                  cartHasNoProducts ||
                  (getStep() === 1 && !projectIdDisabled && !projectIdSelected)
                "
              >
                {{ $t("ordering.next") }}
              </c-button>
            </router-link>

            <c-button
              v-if="getStep() === 4"
              background="brand.beige"
              borderColor="brand.beige"
              :class="isOrderingAllowed ? false : 'disabled'"
              :disabled="!isOrderingAllowed"
              float="right"
              type="submit"
              grid-area="next"
              :ml="{ base: 'auto', md: '0' }"
              data-cy="send-order-button"
            >
              {{ $t("ordering.buttons.send") }}
            </c-button>
          </c-box>
        </form>
      </div>
    </c-box>
  </div>
</template>

<script lang="ts">
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import deliveryTypes from "@/components/DeliveryTypes";
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import MobileTabMenu from "@/components/MobileTabMenu.vue";
import CheckAvailability from "@/components/ordering/CheckAvailability.vue";
import config from "@/config";
import Heading from "@/design-system/elements/Heading.vue";
import Icon from "@/design-system/elements/Icon.vue";
import { IPartnerInfoState } from "@/store/partnerInfoModule";
import { IProductDataState } from "@/store/productDataModule";
import { OrderingBlockedError } from "@/store/shoppingCartModule";
import { getPhoneNumberInfo } from "@/util/PhoneNumber";
import calculateCartSum, { roundToCents } from "@/util/PriceUtil";
import { isSurfaceTreatmentFeeRequired, productToOrderRow } from "@/util/ProductUtil";
import ShoppingCart from "@/views/ShoppingCart.vue";
import {
  CBox,
  CButton,
  CDivider,
  CFlex,
  CHeading,
  CIcon,
  CList,
  CListItem,
  CStack,
  CSwitch,
  CText,
} from "@chakra-ui/vue";
import {
  countriesWithoutPostalCodesList,
  countryList,
  countrySubdivisionList,
  currentStep,
  ErrorMessages,
  IDeliveryDateInformationRequest,
  IOrder,
  IOrderRow,
  Logger,
  ResponseStatus,
  rules,
  SubsidiaryIds,
} from "@iloq/shared";
import { AxiosError } from "axios";
import _, { cloneDeep } from "lodash";
import { defineComponent } from "vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import Datepicker from "vuejs-datepicker";
import { email, helpers, maxLength, minLength, numeric, required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import AddressValidationApi from "../api/AddressValidationApi";
import OrderTabContainer from "../components/ordering/OrderTabContainer.vue";
import { IRootState } from "@iloq/shared";
import { nonDeliveryDates } from "../util/DateUtil";
import AdditionalServices from "./ordering/AdditionalServices.vue";
import DeliveryDetails from "./ordering/DeliveryDetails.vue";
import OrderingConfirmation from "./ordering/OrderingConfirmation.vue";

// @ts-expect-error helpers.regex does exist
const alphanumericSpacesAndSpecialChars = helpers.regex(
  "alpha",
  rules.regexp.alphaNumericWithSpecialChars,
);
// @ts-expect-error helpers.regex does exist
const printableAsciiWithLatin1SpecialLetters = helpers.regex(
  "alpha",
  rules.regexp.printableAsciiWithLatin1SpecialLetters,
);
// @ts-expect-error helpers.regex does exist
const printableAsciiWithLatin1SpecialLettersAndNewlineAndCurrencySymbols = helpers.regex(
  "alpha",
  rules.regexp.printableAsciiWithLatin1SpecialLettersAndNewlineAndCurrencySymbols,
);
const stateExists = (value: string, siblings: any) =>
  !["US", "CA", "IE"].includes(siblings.countryCode) || (value || "").trim().length >= 2;

const commonValidations = {
  projectIdSelected: {
    required,
  },
  projectIdDisabled: {},
  projectDiscountCode: {
    numeric,
    minLength: minLength(rules.minLength.global.projectId),
    maxLength: maxLength(rules.maxLength.global.projectId),
  },
  phoneNumberInputValue: {
    required,
    maxLength: maxLength(rules.maxLength.global.phoneNumber),
  },
  cart: {
    deliveryDetails: {
      companyName: {
        printableAsciiWithLatin1SpecialLetters,
        maxLength: maxLength(100),
      },
      receiverName: {
        required,
        alphanumericSpacesAndSpecialChars,
        maxLength: maxLength(50),
      },
      email: { required, email, maxLength: maxLength(50) },
      deliveryTypeCode: { required },
      shipToAddress: {
        countryCode: { required, maxLength: maxLength(2) },
        stateProvinceCode: { stateExists },
      },
      requestedDeliveryDate: { required },
      additionalDetails: {
        printableAsciiWithLatin1SpecialLettersAndNewlineAndCurrencySymbols,
        maxLength: maxLength(rules.maxLength.global.additionalDetails),
      },
      reference: {
        alphanumericSpacesAndSpecialChars,
        maxLength: maxLength(rules.maxLength.global.reference),
      },
      externalOrderNumber: {
        maxLength: maxLength(rules.maxLength.global.externalOrderNumber),
      },
    },
  },
  projectInputForValidator: { required },
};

const validateCommonAddress = {
  cart: {
    deliveryDetails: {
      shipToAddress: {
        streetAddress: {
          required,
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress),
        },
        streetAddress2: {
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress2),
        },
        zipCode: {
          required,
          maxLength: maxLength(rules.maxLength.global.zipCode),
        },
        city: {
          required,
          maxLength: maxLength(rules.maxLength.global.city),
        },
      },
    },
  },
};

const validateNonPostalCodeAddress = {
  cart: {
    deliveryDetails: {
      shipToAddress: {
        streetAddress: {
          required,
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress),
        },
        streetAddress2: {
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress2),
        },
        zipCode: {
          numeric,
          maxLength: maxLength(rules.maxLength.finnish.zipCode),
          minLength: minLength(rules.maxLength.finnish.zipCode),
        },
        city: {
          required,
          maxLength: maxLength(rules.maxLength.global.city),
        },
      },
    },
  },
};

const validateFIAddress = {
  cart: {
    deliveryDetails: {
      shipToAddress: {
        streetAddress: {
          required,
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.finnish.streetAddress),
        },
        streetAddress2: {
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.finnish.streetAddress2),
        },
        zipCode: {
          required,
          numeric,
          maxLength: maxLength(rules.maxLength.finnish.zipCode),
          minLength: minLength(rules.maxLength.finnish.zipCode),
        },
        city: {
          required,
          maxLength: maxLength(rules.maxLength.global.city),
        },
      },
    },
  },
};

const validateUSAddress = {
  cart: {
    deliveryDetails: {
      shipToAddress: {
        streetAddress: {
          required,
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress),
        },
        streetAddress2: {
          alphanumericSpacesAndSpecialChars,
          maxLength: maxLength(rules.maxLength.global.streetAddress2),
        },
        zipCode: {
          required,
          maxLength: maxLength(rules.maxLength.global.zipCode),
        },
        city: {
          required,
          maxLength: maxLength(rules.maxLength.global.city),
        },
      },
    },
  },
};

const euCountries = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czechia
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "HU", // Hungary
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SE", // Sweden
];

const countriesWithoutPostalCodes = countriesWithoutPostalCodesList();

const cloneIfDiffers = (key: string, source: any, target: any) => {
  if (!_.isEqual(source[key], target[key])) {
    target[key] = _.cloneDeep(source[key]);
  }
};
const setIfDiffers = (key: string, source: any, target: any) => {
  if (!_.isEqual(source[key], target[key])) {
    target[key] = source[key];
  }
};

const maxProducts = parseInt(config.get("orderMaxProductQuantity"), 10) || 10000;

export default defineComponent({
  name: "OrderContainer",
  mixins: [FormValidationMixin],
  components: {
    Datepicker,
    ShoppingCart,
    VuePhoneNumberInput,
    Heading,
    Icon,
    DeliveryDetails,
    CBox,
    CHeading,
    CFlex,
    CSwitch,
    CText,
    CStack,
    CList,
    CListItem,
    CDivider,
    CButton,
    CIcon,
    AdditionalServices,
    OrderingConfirmation,
    CheckAvailability,
    MobileTabMenu,
    OrderTabContainer,
  },
  data() {
    return {
      cart: {
        deliveryDetails: {},
        discounts: {},
        cartRows: [],
      },
      deliveryDelay: 1,
      checkingAvailability: false,
      projectInputForValidator: "no project",
      confirm: false,
      confirmCustomerReference: false,
      datePickerState: {
        disabledDates: {
          to: new Date(),
          days: [6, 0],
          dates: nonDeliveryDates,
        },
      },
      addressValidationApi: new AddressValidationApi(),
      submitting: false,
      phoneNumberInputValue: "",
      projectDiscountCode: "",
      projectIdDisabled: "",
      projectIdSelected: false,
    };
  },
  validations() {
    const validations = {
      ...commonValidations,
      cart: {
        ...commonValidations.cart,
      },
    };

    const country = this.cart.deliveryDetails.shipToAddress.countryCode;
    const isCountryWithoutPostalCodes = countriesWithoutPostalCodes.includes(country);
    const result = {};
    if (isCountryWithoutPostalCodes) {
      _.merge(result, validations, validateNonPostalCodeAddress);
    } else {
      switch (country) {
        case "US":
          _.merge(result, validations, validateUSAddress);
          break;
        case "FI":
          _.merge(result, validations, validateFIAddress);
          break;
        default:
          _.merge(result, validations, validateCommonAddress);
      }
    }
    return result;
  },
  watch: {
    "cart.cartRows": {
      handler() {
        if (this.storeCart.hydrated) {
          if (this.cartHasNoProducts) {
            this.$router.replace({
              query: {
                step: "1",
              },
            });
          }
        }
      },
      deep: true,
    },
    "storeCart.deliveryDetails": {
      handler() {
        cloneIfDiffers("deliveryDetails", this.storeCart, this.cart);
        this.revalidateDeliveryDetails();
      },
      deep: true,
    },
    "storeCart.discounts": {
      handler() {
        cloneIfDiffers("discounts", this.storeCart, this.cart);
        this.revalidateProjectDetails();
      },
      deep: true,
    },
    "storeCart.cartRows": {
      handler() {
        setIfDiffers("cartRows", this.storeCart, this.cart);
        this.updateDeliveryDelay();
      },
      deep: true,
    },
    "cart.deliveryDetails": {
      handler() {
        this.getDeliveryMethodList();

        if (!_.isEqual(this.cart.deliveryDetails, this.storeCart.deliveryDetails)) {
          this.$store.dispatch(
            "shoppingCart/setDeliveryDetails",
            _.cloneDeep(this.cart.deliveryDetails),
          );
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user,
    }),
    ...mapState({
      storeCart: (state: unknown) => (state as IRootState).shoppingCart,
      partnerInfo: (state: any) => (state as IRootState).partnerInfo,
      shoppingCartNotEmpty: (state: any): boolean =>
        ((state as IRootState).shoppingCart?.cartRows?.length ?? 0) > 0,
    }),
    ...mapState("partnerInfo", {
      deliveryTypes: (state: unknown) => (state as IPartnerInfoState).deliveryTypes,
    }),
    ...mapState("productData", {
      handlingFee: (state: unknown) => (state as IProductDataState).handlingFee,
      surfaceTreatmentFee: (state: unknown) => (state as IProductDataState).surfaceTreatmentFee,
      serviceproducts: (state: unknown) => (state as IProductDataState).serviceproducts.products,
    }),
    cartHasNoProducts() {
      const serviceproductIds = this.$store.state.productData.serviceproducts.ids;
      const hasProducts = Boolean(
        (
          this.cart.cartRows?.filter(
            (row: IOrderRow) => !serviceproductIds.includes(row?.product?.id),
          ) || []
        ).length,
      );
      return !hasProducts;
    },
    isUserPermittedToOrderServiceProducts() {
      return (this.$store.state.productData.serviceproducts.ids || []).length > 0;
    },
    phoneNumberWithoutCountryCallingCode(): string {
      if (this.phoneNumberInputValue) {
        return this.phoneNumberInputValue;
      }

      if (this.phoneNumberInputValue === null) {
        return "";
      }

      if (!this.cart.deliveryDetails.phoneNumber) {
        return "";
      }

      return getPhoneNumberInfo(
        this.cart.deliveryDetails.phoneNumber,
        this.cart.deliveryDetails.shipToAddress.countryCode,
      ).phoneNumberWithoutCountryCallingCode;
    },
    defaultPhoneNumberCountryCode(): string {
      return getPhoneNumberInfo(
        this.cart.deliveryDetails.phoneNumber,
        this.cart.deliveryDetails.shipToAddress.countryCode,
      ).countryCode;
    },
    shipToCountryList(): any {
      const countries = countryList();
      return countries;
    },
    isOrderTooBig(): boolean {
      let products = 0;
      this.cart.cartRows.forEach((row: any) => {
        products += row.quantity;
      });
      return products > maxProducts;
    },
    isOrderingAllowed(): boolean {
      return (
        this.confirm &&
        this.confirmCustomerReference &&
        !this.$v.$invalid &&
        this.cart.cartRows.length > 0 &&
        !this.isOrderTooBig
      );
    },
    isStoringAsDraftAllowed(): boolean {
      return this.cart.cartRows.length > 0 && !this.isOrderTooBig;
    },
    translatedCurrentPage(): string {
      switch (this.getStep()) {
        case 1:
          return `1. ${this.$t("shoppingCart.title")}`;
        case 2:
          return `2. ${this.$t("ordering.serviceProducts.title")}`;
        case 3:
          return `${this.isUserPermittedToOrderServiceProducts ? "3." : "2."} ${this.$t(
            "ordering.delivery.title",
          )}`;
        case 4:
          return `${this.isUserPermittedToOrderServiceProducts ? "4." : "3."} ${this.$t(
            "ordering.overview.title",
          )}`;
        default:
          throw new Error("Invalid step");
      }
    },
    shouldBeValidatedWithDHLEndpoint(): boolean {
      const useDHL = this.$store?.state?.features?.useDHLInAddressValidation;
      return !!useDHL;
    },
  },
  methods: {
    getDeliveryMethodList(): any {
      const result: any[] = [];
      this.deliveryTypes.forEach((deliveryType) => {
        result.push({ v: deliveryType.code, text: deliveryType.name });
      });
      const countryCode = this.cart.deliveryDetails?.shipToAddress?.countryCode;
      if (this.user.subsidiaryId === SubsidiaryIds.USA) {
        result.push(...deliveryTypes().forIloqUsaInc);
      } else if (countryCode) {
        if (countryCode === "FI") {
          result.push(...deliveryTypes().forFinland);
        } else if (["NO", "AD", "CH", "GB"].includes(countryCode)) {
          result.push(...deliveryTypes().forNorwayAndorraSwitzerlandAndUnitedKingdom);
        } else if (euCountries.indexOf(countryCode) >= 0) {
          result.push(...deliveryTypes().forEu);
        } else {
          result.push(...deliveryTypes().forOthers);
        }
      }

      const selectedDeliveryMethodStillExists = result.find(
        (deliveryType: any) => deliveryType.v === this.cart.deliveryDetails.deliveryTypeCode,
      );
      if (!selectedDeliveryMethodStillExists) {
        this.cart.deliveryDetails.deliveryTypeCode = "";
      }

      return result;
    },
    saveDraft(): void {
      const order = this.buildOrder(this.storeCart);
      this.submitting = true;
      this.$store
        .dispatch("shoppingCart/storeOrderAsDraft", order)
        .then(() => {
          this.$store.commit("showGeneralInfo", {
            dialogTitle: this.$t("ordering.buttons.saveDraft"),
            dialogText: this.$t("ordering.saveDraftConfirmed"),
          });

          this.$router.push({
            name: "orders",
          });
          this.$store.commit("shoppingCart/removeFromLocalStorage");
          this.$store.dispatch("shoppingCart/initializeShoppingCart");
        })
        .catch((err) => {
          this.$store.commit(
            "showGeneralError",
            {
              dialogTitle: this.$t("ordering.saveDraftError.title"),
              dialogText: this.$t("ordering.saveDraftError.text"),
              error: err,
            },
            { root: true },
          );
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    continueShopping(): void {
      this.$router.push({ path: "/products" });
    },
    updateDeliveryDelay(): Promise<void> {
      const products = this.$store.state.shoppingCart.cartRows.map((cartRow: IOrderRow) => {
        return cartRow.product;
      });
      const deliveryDateInformationRequest: IDeliveryDateInformationRequest = {
        deliveryCountry: this.user.countryCode,
        products: products,
      };
      Logger.debug("updateDeliveryDelay");
      this.$store
        .dispatch("deliveryDate/getDeliveryDateInformation", deliveryDateInformationRequest)
        .then((deliveryDateInformation) => {
          const setDefaultRequestedDeliveryDate = (): void => {
            if (!this.cart.deliveryDetails.requestedDeliveryDate) {
              Logger.debug(
                `setDefaultRequestedDeliveryDate, ${deliveryDateInformation.deliveryDate}`,
              );
              this.cart.deliveryDetails.requestedDeliveryDate =
                deliveryDateInformation.deliveryDate;
            }
          };
          setDefaultRequestedDeliveryDate();
          this.datePickerState.disabledDates.to = deliveryDateInformation.deliveryDate;
          const unsetRequestedDeliveryDateIfNotApplicable = (): void => {
            if (
              this.cart.deliveryDetails.requestedDeliveryDate &&
              this.cart.deliveryDetails.requestedDeliveryDate.getTime() <
                deliveryDateInformation.deliveryDate.getTime()
            ) {
              Logger.debug(
                `Setting requestedDeliveryDate as undefined, this.cart.deliveryDetails.requestedDeliveryDat=${this.cart.deliveryDetails.requestedDeliveryDate}, disabledTo=${deliveryDateInformation.deliveryDate}`,
              );
              this.cart.deliveryDetails.requestedDeliveryDate = undefined;
            }
          };
          unsetRequestedDeliveryDateIfNotApplicable();
        });
      return Promise.resolve();
    },
    setPhoneNumber(payload: any) {
      if (payload.formatInternational)
        this.cart.deliveryDetails.phoneNumber = payload.formatInternational;
    },
    setPhoneNumberInput(phoneNumber: string) {
      this.phoneNumberInputValue = phoneNumber;
      this.onFieldChange("phoneNumberInputValue");
    },
    setProjectDiscountCode(projectDiscountCode: string) {
      this.projectDiscountCode = projectDiscountCode;
      this.onFieldChange("projectDiscountCode");
    },
    setProjectIdDisabled(projectIdDisabled: string) {
      this.projectIdDisabled = projectIdDisabled;
      this.onFieldChange("projectIdDisabled");
    },
    setProjectIdSelected(projectIdSelected: boolean) {
      this.projectIdSelected = projectIdSelected;
      this.onFieldChange("projectIdSelected");
    },
    revalidateDeliveryDetails() {
      [
        "cart.deliveryDetails.companyName",
        "cart.deliveryDetails.receiverName",
        "cart.deliveryDetails.shipToAddress.streetAddress",
        "cart.deliveryDetails.shipToAddress.streetAddress2",
        "cart.deliveryDetails.shipToAddress.zipCode",
        "cart.deliveryDetails.shipToAddress.city",
        "cart.deliveryDetails.shipToAddress.stateProvinceCode",
        "cart.deliveryDetails.shipToAddress.countryCode",
        "cart.deliveryDetails.email",
        "cart.deliveryDetails.deliveryTypeCode",
        "cart.deliveryDetails.requestedDeliveryDate",
        "cart.deliveryDetails.reference",
        "cart.deliveryDetails.externalOrderNumber",
        "cart.deliveryDetails.additionalDetails",
        "phoneNumberInputValue",
      ].forEach((field) => {
        this.onFieldBlur(field);
      });
    },
    revalidateProjectDetails() {
      ["projectDiscountCode", "projectInputForValidator"].forEach((field) => {
        this.onFieldBlur(field);
      });
    },
    async submit(): Promise<void> {
      Logger.debug("sendOrder called");

      await this.updateDeliveryDelay();
      this.$v.$touch();
      if (this.$v.$invalid) {
        Logger.debug("Form had validation errors");
        this.$router.push({ path: "/shopping_cart", query: { step: "3" } });
        return;
      }
      const cart = cloneDeep(this.storeCart);

      const { cartRows } = cart;
      const serviceproductIds = this.$store.state.productData.serviceproducts.ids;

      for (const row of cartRows) {
        const isServiceproduct = serviceproductIds.includes(row.product.id);
        const paymentTermsCodeSuffix: string | null = row.product?.payment_terms_code_suffix;

        if (paymentTermsCodeSuffix) {
          cart.paymentTermsCode = `${this.partnerInfo.partnerInfo.paymentTermsCode}${paymentTermsCodeSuffix}`;
        }

        if (isServiceproduct) {
          row.product.price.price = roundToCents(cart.totalSum.price * row.product.price_factor);
          row.rowTotalSum = row.product.price.price;
        }
      }

      const hasFreeHandling = this.$store.state.partnerInfo?.partnerInfo?.hasFreeHandling;

      if (!hasFreeHandling && this.handlingFee !== null) {
        cartRows.push(productToOrderRow({ ...this.handlingFee, availability: "AVAILABLE" }));
      }
      if (this.surfaceTreatmentFee !== null && isSurfaceTreatmentFeeRequired(cartRows)) {
        cartRows.push(
          productToOrderRow({ ...this.surfaceTreatmentFee, availability: "AVAILABLE" }),
        );
      }

      const { totalSum, rows } = calculateCartSum(cartRows, cart.discounts, serviceproductIds);
      cart.totalSum = totalSum;
      cart.cartRows = rows;

      Logger.debug("Form did not have validation errors");
      this.submitting = true;

      if (cart.deliveryDetails.shipToAddress.stateProvinceCode) {
        const getCountrySubdivisionByCode = (countryCode: string, subdivisionCode: string) =>
          countrySubdivisionList(countryCode)!.find((item: any) => item.v === subdivisionCode)?.t;
        cart.deliveryDetails.shipToAddress.stateProvince = getCountrySubdivisionByCode(
          cart.deliveryDetails.shipToAddress.countryCode,
          cart.deliveryDetails.shipToAddress.stateProvinceCode,
        );
      }

      this.addressValidationApi
        .validateAddressAndGetCities(
          cart.deliveryDetails.shipToAddress.zipCode,
          cart.deliveryDetails.shipToAddress.countryCode,
          this.shouldBeValidatedWithDHLEndpoint,
          false,
          cart.deliveryDetails.shipToAddress.city,
        )
        .then(async (cities: string[]) => {
          if (
            !cities.includes(cart.deliveryDetails.shipToAddress.city) &&
            !countriesWithoutPostalCodes.includes(cart.deliveryDetails.shipToAddress.countryCode)
          ) {
            Logger.error(
              `Selected city '${
                cart.deliveryDetails.shipToAddress.city
              }' not found from the cities list '${cities.join(", ")}'`,
            );
            this.submitting = false;
            this.$router.push({ path: "/shopping_cart", query: { step: "3" } });
            this.addFormError(
              "cart.deliveryDetails.shipToAddress.city",
              "common.address.city.notSelectedFromListOfCities",
            );
            return Promise.resolve();
          }

          this.removeFormError("zipCode");
          const order: IOrder = this.buildOrder(cart);
          Logger.debug("Address valid, calling sendOrder with order=", order);

          return this.$store
            .dispatch("shoppingCart/sendOrder", order)
            .then((orderCreationResponse: IOrder) => {
              Logger.debug("Dispatched sendOrder call returned, data=", orderCreationResponse);
              this.$store
                .dispatch("orders/fetchOrders")
                .then(() => {
                  Logger.debug("savedOrder.orderNumber=", orderCreationResponse.orderNumber);
                  if (orderCreationResponse.orderNumber) {
                    this.$router.push({
                      name: "view_order",
                      params: {
                        id: orderCreationResponse.orderNumber,
                        isConfirmationForNewOrder: "true",
                      },
                    });
                    this.$store.commit("shoppingCart/removeFromLocalStorage");
                    this.$store.dispatch("shoppingCart/initializeShoppingCart");
                  }
                })
                .catch((err) => {
                  Logger.error("Fetching orders failed after sending order", err);
                  this.confirm = false;
                  this.confirmCustomerReference = false;
                  this.$store.commit("shoppingCart/removeFromLocalStorage");
                  this.$store.dispatch("shoppingCart/initializeShoppingCart");
                });
            });
        })
        .catch((err) => {
          if (err instanceof OrderingBlockedError) {
            return Promise.resolve();
          }

          const axiosError = err as AxiosError;

          if (axiosError.response?.status === ResponseStatus.notFound) {
            Logger.error(
              `Address is not valid, city not found with given zip code '${cart.deliveryDetails.shipToAddress.zipCode}' and country code '${cart.deliveryDetails.shipToAddress.countryCode}'`,
            );
            this.submitting = false;
            this.$router.push({ path: "/shopping_cart", query: { step: "3" } });
            this.addFormError("cart.deliveryDetails.shipToAddress.city", null);
            return Promise.resolve();
          }

          if (
            axiosError.response?.status === 500 &&
            axiosError.response.data?.error === ErrorMessages.OPERATION_NOT_ALLOWED_FOR_PARTNER
          ) {
            return this.$store.commit("showGeneralError", {
              dialogTitle: this.$t("ordering.orderingBlocked.title"),
              dialogText: this.$t("ordering.orderingBlocked.text"),
            });
          }
          return handleCommonApiErrors(err);
        })
        .catch((err: any) => {
          Logger.error("Sending order failed", err);

          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("ordering.generalError.title"),
            dialogText: this.$t("ordering.generalError.text"),
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    updateFormError(field: string, message: string | null) {
      this.removeFormError(field);
      this.addFormError(field, message);
    },
    async handleCountryAndZipCodeValidationAndCityUpdate() {
      this.cart.deliveryDetails.shipToAddress.cities = [];
      if (
        countriesWithoutPostalCodes.includes(this.cart.deliveryDetails.shipToAddress.countryCode)
      ) {
        this.removeFormError("cart.deliveryDetails.shipToAddress.zipCode");
        this.removeFormError("cart.deliveryDetails.shipToAddress.city");
        this.cart.deliveryDetails.shipToAddress.zipCode = "00000";
        this.cart.deliveryDetails.shipToAddress.city = "";
        return;
      } else if (
        !this.cart.deliveryDetails.deliveryTypeCode ||
        !this.cart.deliveryDetails.shipToAddress.countryCode ||
        !this.cart.deliveryDetails.shipToAddress.zipCode
      ) {
        return this.updateFormError("cart.deliveryDetails.shipToAddress.city", null);
      }

      try {
        const cities = await this.addressValidationApi.validateAddressAndGetCities(
          this.cart.deliveryDetails.shipToAddress.zipCode,
          this.cart.deliveryDetails.shipToAddress.countryCode,
          this.shouldBeValidatedWithDHLEndpoint,
          false,
          this.cart.deliveryDetails.shipToAddress.city,
        );

        this.cart.deliveryDetails.shipToAddress.cities = cities;
        if (cities.length === 1) {
          this.cart.deliveryDetails.shipToAddress.city = cities[0];
          this.removeFormError("cart.deliveryDetails.shipToAddress.city");
        } else {
          if (
            this.cart.deliveryDetails.shipToAddress.cities.includes(
              this.cart.deliveryDetails.shipToAddress.city,
            )
          ) {
            this.removeFormError("cart.deliveryDetails.shipToAddress.city");
          } else {
            this.cart.deliveryDetails.shipToAddress.city = "";
            this.updateFormError(
              "cart.deliveryDetails.shipToAddress.city",
              "common.address.city.notSelectedFromListOfCities",
            );
          }
        }
      } catch (error) {
        const err = error as AxiosError;
        this.cart.deliveryDetails.shipToAddress.city = "";
        this.cart.deliveryDetails.shipToAddress.cities = [];

        if (err.response?.status === ResponseStatus.notFound) {
          Logger.debug(err);

          this.updateFormError(
            "cart.deliveryDetails.shipToAddress.city",
            "common.address.city.notFoundWithDeliveryMethod",
          );
        } else {
          Logger.error(err);
          this.updateFormError(
            "cart.deliveryDetails.shipToAddress.city",
            "common.address.city.unknownError",
          );
        }
        throw err;
      }
    },
    buildOrder(cart: any): IOrder {
      const { cities, ...restOfShipToAddress } = cart.deliveryDetails?.shipToAddress ?? {};

      const order: IOrder = {
        languageCode: this.user.languageCode,
        discounts: cart.discounts,
        partnerId: "",
        subsidiaryId: "",
        currencyCode: cart.cartRows[0].product.price.currencyCode,
        deliveryDetails: {
          ...cart.deliveryDetails,
          shipToAddress: {
            ...restOfShipToAddress,
          },
        },
        rows: cart.cartRows,
        totalSum: cart.totalSum,
        paymentTermsCode: cart.paymentTermsCode,
        projectIdDisabled: cart.projectIdDisabled,
      };
      return order;
    },
    getNextStep() {
      const current = this.getStep();
      if (current === 1 && !this.isUserPermittedToOrderServiceProducts) {
        return 3;
      }
      if (current < 4) {
        return current + 1;
      }
      return current;
    },
    getPreviousStep() {
      const current = this.getStep();
      if (current === 3 && !this.isUserPermittedToOrderServiceProducts) {
        return 1;
      }
      if (current > 1) {
        return current - 1;
      }
      return current;
    },
    getStep() {
      const queryStep = this.$route.query?.step;
      return currentStep(queryStep as string, 4);
    },
  },
  beforeMount() {
    this.cart.deliveryDetails = _.cloneDeep(this.storeCart.deliveryDetails);

    const setDefaultRequestedDeliveryDate = (): void => {
      if (!this.cart.deliveryDetails.requestedDeliveryDate) {
        const deliveryDateInformationRequest: IDeliveryDateInformationRequest = {
          deliveryCountry: this.user.countryCode,
          products: [],
        };
        this.$store
          .dispatch("deliveryDate/getDeliveryDateInformation", deliveryDateInformationRequest)
          .then((deliveryDateInformation) => {
            Logger.debug(
              `Setting default requestedDeliveryDate=${deliveryDateInformation.deliveryDate}`,
            );
            this.cart.deliveryDetails.requestedDeliveryDate = deliveryDateInformation.deliveryDate;
          });
      }
    };

    setDefaultRequestedDeliveryDate();

    this.revalidateDeliveryDetails();
    this.cart.discounts = _.cloneDeep(this.storeCart.discounts);
    this.revalidateProjectDetails();
    this.cart.cartRows = this.storeCart.cartRows;
    this.updateDeliveryDelay();
  },
  beforeUpdate() {
    if (!this.phoneNumberInputValue) {
      const num = this.phoneNumberWithoutCountryCallingCode;
      if (num) {
        this.phoneNumberInputValue = num;
        this.revalidateDeliveryDetails();
      }
    }
  },
});
</script>

<style lang="scss">
@media print {
  @import "../design-system/assets/tokens/tokens";
  body {
    margin: 0;
    color: #000;
    background-color: #fff;
  }
  .button-print {
    display: none;
  }
  .shopping_cart {
    .main-row {
      position: relative;
      height: auto;
      display: block;
      overflow: hidden;
      .wrapper {
        margin-top: 0;
      }
    }
    .top-bar {
      display: none;
    }
    .sidebar {
      position: absolute;
      top: 0;
      right: 0;
      display: block;
      .sidebar__nav {
        box-shadow: 0 0 0 rgba(255, 255, 255, 0);
        border: 0;
        min-height: 0;
        height: auto;
        position: absolute;
        margin: 0;
        top: 0;
        right: 0;
        background: transparent;
        .logo {
          padding-right: 1.5cm;
        }
      }
      .nav {
        display: none;
      }
      .sidebar__content {
        padding: 0;
        text-align: right;
      }
    }
    .orderingForm {
      display: block;
      margin: 20px;
      margin-top: 20px !important;
      .heading {
        margin: 10px 0 5px;
        padding: 0;
      }
      .quick-search {
        display: none;
        visibility: hidden;
      }
      .shoppingCartSection,
      .deliveryDetailsSection,
      .projectDetailsSection {
        // border-radius: 0;
        // border-color: $color-iloq-gray-light;
        margin: 0;
        padding: 0;
        box-shadow: 0 0 0 rgba(255, 255, 255, 0);
        .cart__row {
          padding: 15px 0px;
          page-break-inside: avoid;
          .cart__section {
            .cart__section--label {
              display: none;
            }
          }
          &--header {
            display: grid;
            padding-bottom: 5px;
            .cart__section {
              .cart__section--label {
                display: block;
              }
              .cart__section--label,
              .cart__section--qty,
              .cart__section--title,
              .cart__section--id,
              .cart__section--availability,
              .cart__section--label,
              .price__value,
              .price__value--old {
                font-size: 10px !important;
              }
            }
          }
        }
        .cart__section--image,
        .cart__section--action {
          display: none;
          visibility: hidden;
          grid-column: unset;
        }
        .cart__section--qty {
          grid-row: 1;
          grid-column: 1/2;
          input {
            border: 0;
            padding: 0 !important;
            display: inline;
            width: auto;
            text-align: left;
            font-weight: bold;
          }
        }
        .cart__section--title {
          grid-column: 2/11;
        }
        .cart__section--id {
          grid-column: 11/16;
        }
        .cart__section--availability {
          grid-column: 11/16;
        }
        .cart__section--price {
          grid-row: 1;
          grid-column: 16/20;
          text-align: right;
          .price {
            text-align: right;
          }
        }
        .cart__section--total-price {
          grid-row: 1;
          grid-column: 20/24;
          text-align: right;
          .price {
            text-align: right;
          }
        }
        .cart__section--label,
        .cart__section--qty,
        .cart__section--title,
        .cart__section--id,
        .cart__section--availability,
        .cart__section--label,
        .price__value,
        .price__value--old {
          font-size: 12px;
          .pointer {
            font-size: 12px;
            white-space: unset;
          }
        }
        .product-note,
        .field-note {
          font-size: 10px;
        }
        .data_block {
          line-height: 1.4;
        }
        .progress-wrapper {
          display: none;
          width: 0;
        }
      }
      .col-md-6 {
        -ms-flex: 0 0 50%;
        flex: 0 0 50%;
        max-width: 50%;
      }
      .offset-lg-5 {
        margin-left: 50%;
      }
      .form-group {
        margin: 0;
        label {
          font-size: 10px !important;
          font-weight: normal;
        }
        input,
        select,
        textarea {
          border: 0;
          padding: 0 !important;
          background-image: none !important;
          font-size: 12px !important;
          font-weight: bold;
          height: auto;
        }
      }
      #additionalDetails {
        border: 0;
        padding: 0 !important;
      }
    }
  }
  .buttons,
  .req,
  #__lpform_receiverName,
  .confirm {
    display: none;
    visibility: hidden;
  }
}
</style>
